/**
 * AR Balloon Pop — port of cristi-boboc/ar-game/ar-balloon-game-FM7j2 to the
 * platform SDK.
 *
 * Architecture (different from the original):
 *
 * - **Independent simMode** — every peer runs its own balloon simulation
 *   from the shared seed (broadcast in the WELCOME packet). The seeded RNG
 *   guarantees that each peer spawns the same balloons in the same
 *   positions at the same times, so the competition is fair without ever
 *   exchanging spawn or pop messages over the wire.
 *
 * - **Camera passthrough** — the SDK's `CameraPassthrough` mounts a front
 *   camera <video> behind a transparent Phaser canvas. Each player sees
 *   their own room as the AR background.
 *
 * - **Hand tracking** — the SDK's `HandTracker` (MediaPipe Tasks Vision)
 *   exposes 21 landmarks per hand. We detect a fist by counting curled
 *   fingers via `getFingerCurl()` and trigger a "squeeze" on the open →
 *   closed transition (with a 200ms cooldown so a single fist doesn't
 *   fire repeatedly).
 *
 * - **Leaderboard** — the SDK's `Leaderboard` helper handles the report →
 *   aggregate → broadcast pattern. When the local player pops a balloon,
 *   we call `lb.report(1)` and the host re-broadcasts the new standings.
 *
 * The visuals are deliberately Phaser-primitive (Graphics) so the game
 * ships zero asset dependencies and runs on any device with WebGL or
 * Canvas. The original used a hand-drawn canvas pipeline; this port uses
 * Phaser shapes + a single per-frame Graphics redraw.
 */
import { SdkGameScene, Leaderboard, SceneCamera, PageBackground } from '@platform/sdk';

// ───────────────────────────────────────────────────────────── tuning

const MATCH_DURATION_SEC = 60;

const SPAWN_INTERVAL_START = 1.5;     // seconds between spawns at t=0
const SPAWN_INTERVAL_END   = 0.4;     // seconds between spawns at t=duration
const MAX_BALLOONS_ON_SCREEN = 6;
const MIN_SPAWN_SPACING_NORM = 0.14;  // min normalized x distance between fresh balloons
const SPAWN_TOP_BAND = 0.35;          // only enforce spacing in the top 35% of the screen

const BALLOON_SPEED_MIN = 0.08;       // normalized y/sec
const BALLOON_SPEED_MAX = 0.16;
const BALLOON_SIZE_MIN = 0.035;       // normalized radius
const BALLOON_SIZE_MAX = 0.055;
const BALLOON_HIT_RADIUS = 0.08;      // additive hit slop

const FIST_CURL_THRESHOLD = 0.6;      // per-finger curl > this counts as closed
const FIST_FINGERS_REQUIRED = 3;      // 3+ closed fingers = fist
const FIST_COOLDOWN_MS = 200;

const BALLOON_COLORS = [
  0xff6b6b, 0xff8e8e, 0xffd93d, 0xffe66d,
  0x6bcb77, 0x95d5b2, 0x4d96ff, 0x74b9ff,
  0xa855f7, 0xc084fc, 0xf97316, 0xfb923c,
  0xec4899, 0xf472b6, 0x06b6d4, 0x22d3ee
];

// ───────────────────────────────────────────────────────────── scene

export default class GameScene extends SdkGameScene {

  start({ players, seed }) {
    // Transparent background so the camera feed shows through.
    this.cameras.main.setBackgroundColor('rgba(0,0,0,0)');

    // The shell scenes (MainMenu, Lobby…) paint the summer backgrounds
    // onto <body>. Clear that so only the camera feed is visible behind
    // the gameplay, not a faded Phaser letterbox of summer grass.
    PageBackground.clear('#000');

    // Render the camera feed as a scene-level background at depth -100.
    // The SDK's CameraPassthrough owns the hidden <video> element; we
    // read frames into a canvas and mount it inside the Phaser scene.
    // Auto-destroyed on scene shutdown.
    SceneCamera.attach(this);

    // Background music for the whole match. AudioManager.playMusic is
    // a no-op until the user has tapped anywhere (needed to unlock the
    // WebAudio context), so the first tap in the lobby already unlocks
    // it — by the time we get here it starts cleanly.
    if (this.sdk.audio) this.sdk.audio.playMusic('music');

    // ── Match state ──────────────────────────────────────────────
    /** @type {Map<number, object>} */
    this.balloons = new Map();
    this.nextBalloonId = 0;
    this.lastSpawnSec = 0;
    this.elapsed = 0;
    this.matchOver = false;

    // Per-hand fist state for squeeze edge detection.
    this.handState = {
      left:  { wasClosed: false, lastSqueezeMs: 0 },
      right: { wasClosed: false, lastSqueezeMs: 0 }
    };

    // ── Leaderboard ──────────────────────────────────────────────
    this.lb = new Leaderboard({ sdk: this.sdk });
    this.lb.onUpdate(({ scores }) => this._renderHud(scores));

    // ── Rendering layers (drawn in tick()) ───────────────────────
    this.balloonGraphics = this.add.graphics().setDepth(10);
    this.handGraphics = this.add.graphics().setDepth(20);
    /** @type {Array<{x: number, y: number, color: number, size: number, time: number, duration: number, miss: boolean}>} */
    this.popEffects = [];

    // ── HUD ──────────────────────────────────────────────────────
    this._buildHud();
    this._renderHud(this.lb.getAll());
  }

  // ─────────────────────────────────────────────────────────── tick

  tick(dt, _inputs) {
    if (this.matchOver) return;

    this.elapsed += dt;
    const remaining = Math.max(0, MATCH_DURATION_SEC - this.elapsed);
    this._updateTimer(remaining);

    if (remaining <= 0) {
      this._endMatch();
      return;
    }

    this._maybeSpawnBalloon();
    this._updateBalloons(dt);
    this._processHands();
    this._updatePopEffects(dt);
    // NOTE: rendering happens in update(), not here. On a slow device the
    // fixed-timestep loop calls tick() multiple times per frame to catch
    // up with real time — rendering inside tick would repeat the draw
    // work N times per visible frame and make the slowdown worse.
  }

  /** Phaser calls this once per rendered frame. Cheap draw of the
   * current simulation state, no physics. */
  update(_time, _delta) {
    if (this.matchOver) return;
    this._renderFrame();
  }

  // ─────────────────────────────────────────────────────── spawning

  _spawnInterval() {
    // Linearly accelerate from SPAWN_INTERVAL_START to SPAWN_INTERVAL_END.
    const t = Math.min(1, this.elapsed / MATCH_DURATION_SEC);
    return SPAWN_INTERVAL_START + (SPAWN_INTERVAL_END - SPAWN_INTERVAL_START) * t;
  }

  _maybeSpawnBalloon() {
    if (this.balloons.size >= MAX_BALLOONS_ON_SCREEN) return;
    if (this.elapsed - this.lastSpawnSec < this._spawnInterval()) return;

    // Pick an x with spacing from existing top-band balloons.
    let x = 0;
    let valid = false;
    for (let attempt = 0; attempt < 15; attempt++) {
      x = 0.1 + this.sdk.rng.next() * 0.8;
      valid = true;
      for (const b of this.balloons.values()) {
        if (b.y < SPAWN_TOP_BAND && Math.abs(b.x - x) < MIN_SPAWN_SPACING_NORM) {
          valid = false;
          break;
        }
      }
      if (valid) break;
    }
    if (!valid) {
      // Screen too crowded — try again next tick.
      this.lastSpawnSec = this.elapsed;
      return;
    }

    const id = ++this.nextBalloonId;
    this.balloons.set(id, {
      id,
      x,
      y: -0.1,
      speed: this.sdk.rng.floatBetween(BALLOON_SPEED_MIN, BALLOON_SPEED_MAX),
      size:  this.sdk.rng.floatBetween(BALLOON_SIZE_MIN, BALLOON_SIZE_MAX),
      color: this.sdk.rng.pick(BALLOON_COLORS),
      wobbleOffset: this.sdk.rng.next() * Math.PI * 2,
      wobbleSpeed: 1.5 + this.sdk.rng.next(),
      wobbleAmount: 0.008 + this.sdk.rng.next() * 0.006
    });
    this.lastSpawnSec = this.elapsed;
  }

  _updateBalloons(dt) {
    for (const [id, b] of this.balloons) {
      b.y += b.speed * dt;
      b.x += Math.sin(this.elapsed * b.wobbleSpeed + b.wobbleOffset) * b.wobbleAmount * dt;
      if (b.y > 1.3) this.balloons.delete(id);
    }
  }

  // ────────────────────────────────────────────────── hand → squeeze

  _processHands() {
    const tracker = this.sdk.tracking && this.sdk.tracking.hands;
    if (!tracker) return;

    for (const side of ['left', 'right']) {
      const hand = tracker.getHand(side);
      if (!hand) {
        // Hand left the frame — reset state so the next reappearance can fire.
        this.handState[side].wasClosed = false;
        continue;
      }

      const closed = this._isFist(tracker, side);
      const state = this.handState[side];

      if (closed && !state.wasClosed) {
        // open → closed transition: this is a squeeze.
        const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        if (now - state.lastSqueezeMs >= FIST_COOLDOWN_MS) {
          state.lastSqueezeMs = now;
          this._onSqueeze(side, hand);
        }
      }
      state.wasClosed = closed;
    }
  }

  _isFist(tracker, side) {
    let curled = 0;
    for (const finger of ['index', 'middle', 'ring', 'pinky']) {
      const c = tracker.getFingerCurl(side, finger);
      if (c > FIST_CURL_THRESHOLD) curled++;
    }
    return curled >= FIST_FINGERS_REQUIRED;
  }

  /**
   * Palm centroid: average of wrist (0) + the 4 finger MCPs (5, 9, 13, 17).
   * Gives a stable point near the middle of the palm regardless of how the
   * hand is rotated. Returns null if any required landmark is missing.
   */
  _palmCenter(hand) {
    const keys = [0, 5, 9, 13, 17];
    let sx = 0, sy = 0, n = 0;
    for (const i of keys) {
      const lm = hand[i];
      if (!lm) continue;
      sx += lm.x;
      sy += lm.y;
      n++;
    }
    if (n === 0) return null;
    return { x: sx / n, y: sy / n };
  }

  _onSqueeze(side, hand) {
    // Use the palm centroid (wrist + 4 finger MCPs) as the squeeze
    // position — landing in the middle of the hand instead of at the
    // wrist. The camera is mirrored (front-facing + scaleX(-1)), so
    // we flip x to match what the player sees on-screen.
    const palm = this._palmCenter(hand);
    if (!palm) return;
    const x = 1 - palm.x;
    const y = palm.y;

    const hits = this._checkPop(x, y);
    if (hits.length > 0) {
      for (const id of hits) {
        const balloon = this.balloons.get(id);
        if (!balloon) continue;
        this.balloons.delete(id);
        this._spawnPopEffect({
          x: balloon.x, y: balloon.y, color: balloon.color, size: balloon.size, miss: false
        });
        this.lb.report(1);
        if (this.sdk.audio) this.sdk.audio.playSfx('pop');
      }
      // Combo bonus: sparkle on 2+ simultaneous pops.
      if (hits.length >= 2 && this.sdk.audio) {
        this.sdk.audio.playSfx('sparkle');
      }
      // Score jingle every 5 pops for a mini-milestone feel.
      this._popCount = (this._popCount || 0) + hits.length;
      if (this._popCount - (this._lastScoreMilestone || 0) >= 5) {
        this._lastScoreMilestone = Math.floor(this._popCount / 5) * 5;
        if (this.sdk.audio) this.sdk.audio.playSfx('score');
      }
    } else {
      // Miss feedback — quick faded ring + soft bounce sound.
      this._spawnPopEffect({ x, y, color: 0xffffff, size: 0.025, miss: true });
      if (this.sdk.audio) this.sdk.audio.playSfx('bounce', { volume: 0.6 });
    }
  }

  _checkPop(px, py) {
    const hits = [];
    for (const [id, b] of this.balloons) {
      const dx = px - b.x;
      const dy = py - b.y;
      if (Math.sqrt(dx * dx + dy * dy) < BALLOON_HIT_RADIUS + b.size) {
        hits.push(id);
      }
    }
    return hits;
  }

  // ─────────────────────────────────────────────────────── effects

  _spawnPopEffect(e) {
    this.popEffects.push({
      x: e.x,
      y: e.y,
      color: e.color,
      size: e.size,
      time: 0,
      duration: e.miss ? 0.25 : 0.55,
      miss: !!e.miss
    });
  }

  _updatePopEffects(dt) {
    for (let i = this.popEffects.length - 1; i >= 0; i--) {
      this.popEffects[i].time += dt;
      if (this.popEffects[i].time >= this.popEffects[i].duration) {
        this.popEffects.splice(i, 1);
      }
    }
  }

  // ─────────────────────────────────────────────────────── rendering

  _renderFrame() {
    const W = this.scale.width;
    const H = this.scale.height;

    const g = this.balloonGraphics;
    g.clear();
    for (const b of this.balloons.values()) {
      this._drawBalloon(g, b.x * W, b.y * H, b.size * W, b.color);
    }
    for (const e of this.popEffects) {
      this._drawPopEffect(g, e, W, H);
    }

    const hands = this.sdk.tracking && this.sdk.tracking.hands;
    if (hands) this._drawHandsOverlay(this.handGraphics, hands, W, H);
  }

  _drawBalloon(g, x, y, r, color) {
    // Body (oval) — solid fill, dark stroke ring.
    g.fillStyle(color, 1);
    g.fillEllipse(x, y, r * 1.7, r * 2);
    g.lineStyle(2, darken(color, 0.25), 0.9);
    g.strokeEllipse(x, y, r * 1.7, r * 2);

    // Specular highlight.
    g.fillStyle(0xffffff, 0.4);
    g.fillEllipse(x - r * 0.22, y - r * 0.3, r * 0.26, r * 0.44);

    // Knot triangle.
    g.fillStyle(darken(color, 0.4), 1);
    g.beginPath();
    g.moveTo(x - 3, y + r);
    g.lineTo(x + 3, y + r);
    g.lineTo(x, y + r + 7);
    g.closePath();
    g.fillPath();

    // String.
    g.lineStyle(1, 0xffffff, 0.3);
    g.beginPath();
    g.moveTo(x, y + r + 7);
    g.lineTo(x - 4, y + r + 42);
    g.strokePath();
  }

  _drawPopEffect(g, e, W, H) {
    const x = e.x * W;
    const y = e.y * H;
    const p = e.time / e.duration;

    if (e.miss) {
      g.lineStyle(2 * (1 - p), 0xffffff, (1 - p) * 0.5);
      g.strokeCircle(x, y, 15 + p * 50);
      return;
    }

    const r = e.size * W;

    // Expanding ring.
    g.lineStyle(3 * (1 - p), e.color, 1 - p);
    g.strokeCircle(x, y, r * (1 + p * 4));

    // 8-particle burst.
    const N = 8;
    g.fillStyle(e.color, 1 - p);
    for (let i = 0; i < N; i++) {
      const angle = (i / N) * Math.PI * 2 + e.time * 3;
      const dist = r * (1 + p * 4);
      const px = x + Math.cos(angle) * dist;
      const py = y + Math.sin(angle) * dist;
      g.fillCircle(px, py, (1 - p) * 7);
    }

    // Inner flash.
    if (p < 0.25) {
      g.fillStyle(0xffffff, ((0.25 - p) / 0.25) * 0.5);
      g.fillCircle(x, y, r * (1 + p * 2));
    }
  }

  _drawHandsOverlay(g, tracker, W, H) {
    g.clear();

    // Standard MediaPipe hand connections (21 landmarks).
    const CONNECTIONS = [
      [0,1],[1,2],[2,3],[3,4],
      [0,5],[5,6],[6,7],[7,8],
      [5,9],[9,10],[10,11],[11,12],
      [9,13],[13,14],[14,15],[15,16],
      [13,17],[17,18],[18,19],[19,20],
      [0,17]
    ];

    for (const side of ['left', 'right']) {
      const hand = tracker.getHand(side);
      if (!hand) continue;

      // Bones.
      g.lineStyle(2, 0x00ff80, 0.45);
      g.beginPath();
      for (const [a, b] of CONNECTIONS) {
        if (!hand[a] || !hand[b]) continue;
        g.moveTo((1 - hand[a].x) * W, hand[a].y * H);
        g.lineTo((1 - hand[b].x) * W, hand[b].y * H);
      }
      g.strokePath();

      // Joint dots.
      g.fillStyle(0xffffff, 0.7);
      for (const lm of hand) {
        g.fillCircle((1 - lm.x) * W, lm.y * H, 3);
      }

      // Fist / open indicator drawn at the palm centroid so it matches
      // the actual pop hit-point (see _onSqueeze + _palmCenter).
      const palm = this._palmCenter(hand);
      if (palm) {
        const px = (1 - palm.x) * W;
        const py = palm.y * H;
        const closed = this._isFist(tracker, side);
        if (closed) {
          g.lineStyle(3, 0xff6464, 0.8);
          g.strokeCircle(px, py, 22);
          g.fillStyle(0xff6464, 0.6);
          g.fillCircle(px, py, 6);
        } else {
          g.lineStyle(1.5, 0xffff64, 0.25);
          g.strokeCircle(px, py, 18);
        }
      }
    }
  }

  // ──────────────────────────────────────────────────────────── HUD

  _buildHud() {
    this.timerText = this.add.text(this.scale.width / 2, 24, '60', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '40px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 4
    }).setOrigin(0.5, 0).setDepth(2000);

    this.scoreText = this.add.text(20, 24, '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '20px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3,
      lineSpacing: 4
    }).setOrigin(0, 0).setDepth(2000);

    const instructions = (this.sdk.i18n && this.sdk.i18n.t)
      ? this.sdk.i18n.t('balloon.instructions', 'Make a fist near a balloon to pop it!')
      : 'Make a fist near a balloon to pop it!';
    this.statusText = this.add.text(this.scale.width / 2, this.scale.height - 30,
      instructions, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '16px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5, 1).setDepth(2000);
  }

  _updateTimer(remaining) {
    if (this.timerText) this.timerText.setText(String(Math.ceil(remaining)));
  }

  /** Reposition pinned HUD elements when the window is resized / the
   * device is rotated. Balloons themselves use normalized coords so
   * they already reflow; only the fixed-position HUD needs help. */
  onResize(width, height) {
    if (this.timerText)  this.timerText.setPosition(width / 2, 24);
    if (this.statusText) this.statusText.setPosition(width / 2, height - 30);
  }

  _renderHud(scores) {
    if (!this.scoreText) return;
    const lines = scores.map((row, i) => {
      const arrow = i === 0 ? '★ ' : '  ';
      return arrow + row.name + ': ' + row.score;
    });
    this.scoreText.setText(lines.join('\n'));
  }

  // ────────────────────────────────────────────────────────── end

  _endMatch() {
    if (this.matchOver) return;
    this.matchOver = true;

    // Stop the loop and play the finale sound (explode) on every peer.
    if (this.sdk.audio) {
      this.sdk.audio.stopMusic();
      this.sdk.audio.playSfx('explode');
    }

    // Only the host signals the formal end-of-match (clients ignore).
    if (this.sdk.isHost) {
      const all = this.lb.getAll();
      const winner = this.lb.getWinner();
      const scores = {};
      for (const r of all) scores[r.id] = r.score;
      this.sdk.endMatch({
        winnerName: winner ? winner.name : 'tie',
        scores
      });
    }
  }
}

// ─────────────────────────────────────────────────────── helpers

/** Multiply each RGB channel of a 0xRRGGBB integer by `(1 - amt)`. */
function darken(color, amt) {
  const r = ((color >> 16) & 0xff) * (1 - amt);
  const g = ((color >>  8) & 0xff) * (1 - amt);
  const b = ( color        & 0xff) * (1 - amt);
  return ((r & 0xff) << 16) | ((g & 0xff) << 8) | (b & 0xff);
}
